import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';

import { AlertOutlineIcon, CloseIcon, PlusIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import { getMenuPermissions } from 'Helper/getMenuPermissions';
import { useUserInfo } from 'Store/userInfo';
import ButtonSolid from 'UiKit/button/buttonSolid';
import SearchComponent from 'UiKit/search';
import FilterSelect from 'UiKit/filter-select';
import { TextSemiBold } from 'UiKit/typography';
import Tooltip from 'UiKit/tooltip';

import useGetBrand from 'Domain/brand/useGetBrand';
import useGetPublishStatuses from 'Domain/brief-live/useGetPublishStatuses';
import useGetTalkingPointStatuses from 'Domain/brief-live/useGetTalkingPointStatuses';
import useGetGiveawayStatuses from 'Domain/brief-live/useGetGiveawayStatuses';
import { useBriefLiveState } from 'View/brief-live/state';
import getVisibleFilterByTab from 'View/brief-live/helper/getVisibleFilterByTab';
import checkIsResetAllFilter from 'View/brief-live/helper/checkIsResetAllFilter';
import { briefLiveFilterText } from 'Const/brief-live/view';

import styles from './styles.module.css';

const Filter = ({ selectedTab, t }) => {
  const navigate = useNavigate();
  const { filterState, updateFilterState, resetFilterState, state } = useBriefLiveState();
  const filterConfig = getVisibleFilterByTab(selectedTab);
  const isResetAllFilter = checkIsResetAllFilter({ selectedTab, filterState });
  const { userBrand } = useUserInfo();

  const accessPermission = getMenuPermissions('others', 'briefs');

  const { data: brandOptions, isLoading: isBrandOptionsLoading } = useGetBrand();
  const { data: publishStatusOptions, isLoading: isPublishStatusOptionsLoading } = useGetPublishStatuses();
  const { data: talkingPointStatusOptions, isLoading: isTalkingPointStatusOptionsLoading } = useGetTalkingPointStatuses();
  const { data: giveawayStatusOptions, isLoading: isGiveawayStatusOptionsLoading } = useGetGiveawayStatuses();

  const statusOptions = selectedTab === 'talkingPoint' ? talkingPointStatusOptions : giveawayStatusOptions;
  const createButtonText = selectedTab === 'talkingPoint' ? t("tableTalkingPoint.buttonCreate") : t("tableGiveaway.buttonCreate");
  const createButtonLink = selectedTab === 'talkingPoint' ? '/brief-live/talking-point/upsert' : '/brief-live/giveaway/create';

  const onUpdateQueryParams = ({ type, value }) => {
    updateFilterState({ [selectedTab]: { ...filterState[selectedTab], [type]: value }, page: 1 });
  };

  const onResetFilter = () => resetFilterState();

  useEffect(() => {
    if (!filterState.brand) {
      updateFilterState({ brand: { value: userBrand?.[0] || 'Biodef', label: userBrand?.[0] || 'Biodef' } });
    }
  }, [userBrand, brandOptions]); // eslint-disable-line

  return (
    <Box className={styles['filter__wrapper']}>
      <Flex className={styles['filter__container']}>
        <Flex className={styles['filter-item__container']}>
          <Condition statement={filterConfig?.isBrandFilterVisible}>
            <Box>
              <FilterSelect
                isLoading={isBrandOptionsLoading}
                placeholder={t('filter.brand')}
                value={filterState?.brand}
                width="200px"
                options={brandOptions}
                counterLabel={'Brands'}
                onChange={(value) => updateFilterState({ brand: value, page: 1 })}
              />
            </Box>
          </Condition>

          <Condition statement={filterConfig?.isPromoSearchVisible}>
            <SearchComponent
              width="300px"
              onSearch={(search) => onUpdateQueryParams({ type: 'promoSearch', value: search })}
              value={filterState[selectedTab]?.promoSearch}
            />
          </Condition>

          <Condition statement={filterConfig?.isDraftStatusVisible}>
            <Box>
              <FilterSelect
                isLoading={isPublishStatusOptionsLoading}
                placeholder={t('filter.statusDraft')}
                value={filterState[selectedTab]?.statusDraft}
                clearable={true}
                width="200px"
                options={publishStatusOptions}
                onChange={(value) => onUpdateQueryParams({ type: 'statusDraft', value })}
              />
            </Box>
          </Condition>

          <Condition statement={filterConfig?.isStatusVisible}>
            <Box>
              <FilterSelect
                isLoading={isTalkingPointStatusOptionsLoading || isGiveawayStatusOptionsLoading}
                placeholder={t('filter.status')}
                value={filterState[selectedTab]?.status}
                clearable={true}
                width="200px"
                options={statusOptions}
                onChange={(value) => onUpdateQueryParams({ type: 'status', value })}
              />
            </Box>
          </Condition>

          <Condition statement={isResetAllFilter}>
            <Flex onClick={onResetFilter} className={styles['reset-filter__container']} data-testid="reporting__clear-filter">
              <CloseIcon size="14px" color="var(--color-primary70)" />
              <TextSemiBold color="var(--color-primary70)">Clear Filters</TextSemiBold>
            </Flex>
          </Condition>
        </Flex>

        {/* Create Button */}
        <Flex alignItems={'center'} gap={'8px'}>
          <Condition statement={accessPermission?.isCanCreate}>
            <ButtonSolid
              type="button"
              className={styles['filter-brief-live__button--create']}
              leftIcon={<PlusIcon width="14px" height="14px" />}
              onClick={() => navigate(createButtonLink)}
            >
              {createButtonText}
            </ButtonSolid>
          </Condition>
          <Condition statement={state?.selectedTab === 'giveaway'}>
            <Tooltip label={briefLiveFilterText?.tooltipGiveawayText} placement="right" width="200px">
              <AlertOutlineIcon width="18px" height="18px" color="var(--color-primary70)" />
            </Tooltip>
          </Condition>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Filter;
